import { ChangeEvent, FC, useCallback, useState } from 'react';

import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import { zIndex } from 'styles';

import { sendContactForm } from 'client';
import { validateEmail } from 'components/ContactForm/utils';
import {
  Button,
  EmailInput,
  EmailInputContainer,
  FormControlContainer,
  ModalContent,
} from 'components/Header/ContactModal.style';

type ResourceAccessModalProps = {
  isOpen: boolean;
  onClose: () => void;
  resourceTitle: string;
  resourceUrl: string;
  pageTag: string;
};

export const ResourceAccessModal: FC<ResourceAccessModalProps> = ({
  isOpen,
  onClose,
  resourceTitle,
  resourceUrl,
  pageTag,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [agreeProcessing, setAgreeProcessing] = useState(false);
  const [processingError, setProcessingError] = useState(false);

  const handleClose = useCallback(() => {
    setEmail('');
    setIsEmailValid(true);
    setAgreeMarketing(false);
    setAgreeProcessing(false);
    setProcessingError(false);
    onClose();
  }, [onClose]);

  const onEmailChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setEmail(e.currentTarget.value);
      if (!isEmailValid) setIsEmailValid(true);
    },
    [isEmailValid],
  );

  const onProcessingChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setAgreeProcessing(checked);
    if (checked) setProcessingError(false);
  }, []);

  const onSubmit = useCallback(() => {
    const emailValid = validateEmail(email);
    setIsEmailValid(emailValid);
    setProcessingError(!agreeProcessing);

    if (!emailValid || !agreeProcessing) return;

    setIsLoading(true);

    const message = [
      `Requested resource: ${resourceTitle}`,
      `Resource URL: ${resourceUrl}`,
      `Marketing consent: ${agreeMarketing ? 'yes' : 'no'}`,
      'Data processing consent: yes',
    ].join('\n');

    sendContactForm(email, pageTag, undefined, message)
      .then(() => {
        setIsLoading(false);
        window.open(resourceUrl, '_blank', 'noopener,noreferrer');
        handleClose();
      })
      .catch(() => {
        setIsLoading(false);
        setIsEmailValid(false);
      });
  }, [
    email,
    agreeMarketing,
    agreeProcessing,
    pageTag,
    resourceTitle,
    resourceUrl,
    handleClose,
  ]);

  return (
    <Modal
      sx={{
        zIndex: zIndex.modal,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="resource-access-modal-title"
      aria-describedby="resource-access-modal-description"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(5px)',
            backgroundColor: 'rgba(0,0,0,0.2)',
          },
        },
      }}
    >
      <ModalContent
        sx={{ maxWidth: { xs: 'unset', sm: 560 }, px: { xs: 3, sm: 4 }, py: { xs: 3, sm: 4 } }}
      >
        <Typography id="resource-access-modal-title" variant="h6" component="h2">
          Access This Resource
        </Typography>
        <Typography id="resource-access-modal-description" sx={{ mt: 2 }}>
          Enter your email to receive instant access.
        </Typography>
        <FormControlContainer
          sx={{
            mt: 3,
            gap: 1.5,
            width: '100%',
            flexDirection: 'column',
            alignItems: 'stretch',
            '@media (min-width: 600px)': {
              flexDirection: 'column',
              alignItems: 'stretch',
              flexBasis: 'auto',
            },
          }}
        >
          <EmailInputContainer>
            <EmailInput
              error={!isEmailValid}
              disabled={isLoading}
              fullWidth
              type="email"
              value={email}
              placeholder="Email*"
              onChange={onEmailChange}
              aria-describedby="resource-email-error"
              aria-invalid={!isEmailValid}
              required
            />
            {!isEmailValid && (
              <span id="resource-email-error" className="sr-only">
                Please enter a valid email address.
              </span>
            )}
          </EmailInputContainer>

          <Typography
            variant="body2"
            sx={{ mt: 1, fontSize: 13, lineHeight: 1.5, color: 'text.secondary' }}
          >
            AILiveSim is committed to protecting and respecting your privacy, and we&apos;ll only
            use your personal information to administer your account and to provide the products
            and services you requested from us. From time to time, we would like to contact you
            about our products and services, as well as other content that may be of interest to
            you. If you consent to us contacting you for this purpose, please tick below to say
            how you would like us to contact you:
          </Typography>

          <FormControlLabel
            sx={{ alignItems: 'flex-start', m: 0 }}
            control={
              <Checkbox
                checked={agreeMarketing}
                onChange={(e) => setAgreeMarketing(e.target.checked)}
                disabled={isLoading}
                sx={{ pt: 0.5 }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: 14, lineHeight: 1.5 }}>
                I agree to receive other communications from AILiveSim.
              </Typography>
            }
          />

          <Typography
            variant="body2"
            sx={{ fontSize: 13, lineHeight: 1.5, color: 'text.secondary' }}
          >
            In order to provide you the content requested, we need to store and process your
            personal data. If you consent to us storing your personal data for this purpose,
            please tick the checkbox below.
          </Typography>

          <FormControlLabel
            sx={{ alignItems: 'flex-start', m: 0 }}
            control={
              <Checkbox
                checked={agreeProcessing}
                onChange={onProcessingChange}
                disabled={isLoading}
                required
                sx={{ pt: 0.5 }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: 14, lineHeight: 1.5 }}>
                I agree to allow AILiveSim to store and process my personal data.*
              </Typography>
            }
          />
          {processingError && (
            <Typography variant="caption" color="error">
              You must agree to allow us to store and process your personal data.
            </Typography>
          )}

          <Typography
            variant="caption"
            sx={{ fontSize: 12, lineHeight: 1.5, color: 'text.secondary' }}
          >
            You may unsubscribe from these communications at any time. For more information on
            how to unsubscribe, our privacy practices, and how we are committed to protecting and
            respecting your privacy, please review our{' '}
            <a
              href="http://localhost:3000/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              Privacy Policy
            </a>
            .
          </Typography>

          <Button
            disabled={isLoading}
            size="large"
            onClick={onSubmit}
            aria-label="Submit and access resource"
            sx={{ width: '100% !important', mt: 1 }}
          >
            Submit
          </Button>
        </FormControlContainer>
      </ModalContent>
    </Modal>
  );
};
